import { createUID } from 'common/utils/createUID'
import { getSampleExercises, getSampleExIdsByOrigExIds } from '../utils/sampleExerciseUtils'
import { getSampleProgram, getSampleProgramWkts } from '../utils/programUtils'

import sampleProgramData1 from './newOrgProgram1.json'
import sampleProgramData2 from './newOrgProgram2.json'
import sampleProgramWktsData1 from './newOrgWorkouts1.json'
import sampleProgramWktsData2 from './newOrgWorkouts2.json'

/************
 user
************/

//Written with update() on users/{userId}, so every key is a full path to a leaf. A nested object would
//replace that whole node - a member who already subscribed to another coach would lose that org's
//paidAccess (stripeSubId, stripeCustomerId...), which breaks Manage Billing for them
export const initCoach = (coachOrgId, profile) => {
  const profileUpdates = Object.fromEntries(
    Object.entries({ ...profile, coachOrgId, createdAt: Date.now() }).map(([key, value]) => [`profile/${key}`, value])
  )

  return {
    'metadata/coachOrgId': coachOrgId,
    'metadata/sessionUX': 'gym_workout',
    [`paidAccess/coachOrgs/${coachOrgId}/accessEnd`]: 166146639200000,
    ...profileUpdates,
  }
}

/************
 exercises
 ************/

const newSampleExIdByOrigExId = getSampleExIdsByOrigExIds()

/************
 progressions
 ************/

const sampleExIds = Object.values(newSampleExIdByOrigExId)
const initExProgressions = {
  'sample progression': [sampleExIds[0], sampleExIds[1]],
}

/************
 linkhub
************/

const initLinkHub = {
  description: 'Your message',
  resources: [
    {
      id: createUID(),
      title: 'Sample merch link',
      description: 'Purchase merch and other items',
      link: 'https://www.linkyourmerchstore.com',
    },
    {
      id: createUID(),
      title: 'Sample youtube link',
      description: 'Find my content',
      link: 'https://www.youtube.com',
    },
  ],
}

/************
 workouts
************/

const { sampleWorkouts: programWkts1, newWktIdsToDayIdcs: wktIdsToDayIdcsProgram1 } = getSampleProgramWkts(
  sampleProgramWktsData1,
  newSampleExIdByOrigExId
)
const { sampleWorkouts: programWkts2, newWktIdsToDayIdcs: wktIdsToDayIdcsProgram2 } = getSampleProgramWkts(
  sampleProgramWktsData2,
  newSampleExIdByOrigExId
)
export const initProgramWkts = { ...programWkts1, ...programWkts2 }

/************
 program
************/

export const sampleProgram1 = getSampleProgram(sampleProgramData1, wktIdsToDayIdcsProgram1, programWkts1)
export const sampleProgram2 = getSampleProgram(sampleProgramData2, wktIdsToDayIdcsProgram2, programWkts2)
const initCoachPrograms = {
  [sampleProgram1.id]: sampleProgram1.name,
  [sampleProgram2.id]: sampleProgram2.name,
}

/************
 collections
************/

// Seeded as a collection, not a series: series are the legacy grouping and new
// orgs should never be born with one.
const sampleCollection = {
  color: '#57699b',
  description: 'Sample description',
  id: createUID(),
  name: 'Sample collection',
  previewImg: 'https://d3ts90okmyjsrp.cloudfront.net/samples/thumbnails/JumpRope-NCXpLBgmYmMIzIKFcFd_img.0000000.jpg',
  subtitle: 'Sample subtitle',
  createdAt: Date.now(),
  updatedAt: Date.now(),
  items: {
    [createUID()]: { id: sampleProgram1.id, index: 0, type: 'program' },
    [createUID()]: { id: sampleProgram2.id, index: 1, type: 'program' },
  },
}
const initCollections = { [sampleCollection.id]: sampleCollection }

/************
 explore
************/

const generateExploreData = () => {
  const tabUID = createUID()

  const exploreData = {
    tabContent: {
      [tabUID]: [
        {
          id: createUID(),
          items: [
            {
              id: sampleCollection.id,
              type: 'collection',
            },
            {
              id: sampleProgram1.id,
              type: 'program',
            },
          ],
          name: 'Sample published row',
          visibilityLevel: 'anyone',
        },
      ],
    },
    tabs: {
      [tabUID]: {
        name: 'Explore',
        tabIdx: 0,
        visibilityLevel: 'anyone',
      },
    },
  }

  return exploreData
}

/************
 coachOrg
************/

export const getInitCoachOrg = (coachOrgId, exVidOrientation) => {
  let initCoachOrg = {
    coachOrgId,
    sessionUX: 'gym_workout',
    mobilePreMountSettings: {
      visibleLayout: {
        exerciseLibrary: false,
        linkHub: true,
        upgradePrompt: true,
        workoutsScrollMax: 6,
      },
      contentFilters: {
        difficulty: false,
        equipment: false,
        instructors: false,
        tags: false,
      },
      quiz: {
        enableQuizContentUnlock: false,
      },
      exVidOrientation,
    },
    exSubs: initExProgressions,
    exercises: getSampleExercises(exVidOrientation, newSampleExIdByOrigExId),
    linkHub: initLinkHub,
    collections: initCollections,
    explore: generateExploreData(),
    programs: initCoachPrograms,
    productInfo: {
      accessType: 'coach_org_all_access',
      stripeCustomerOwner: 'connected_account',
      trybeRevSharePercent: 20,
    },
    dashboardOnboarding: {
      exercises: {
        hasReadVidGuidelines: false,
      },
    },
  }

  if (exVidOrientation === 'landscape') {
    initCoachOrg.mobilePreMountSettings.enableLandscapeExAudio = true
  }

  return initCoachOrg
}

/************
 pagebuilder
************/

export const getInitSalesPage = ({ primaryColor, coachName }) => ({
  heroSection: {
    heroCTA: 'Join Today',
    heroHeading: 'Achieve Your Best Shape',
    secondaryText: 'On-demand workouts built to fit your life',
  },
  programSummary: {
    header: 'Benefits',
    highlights: [
      {
        header: 'Train Anytime, Anywhere',
        description: 'Follow guided workouts on your schedule—no gym required.',
      },
      {
        header: 'Expert Coaching',
        description: `Get proven training methods designed by ${coachName}.`,
      },
      {
        header: 'Stay Motivated',
        description: `Track your progress to stay accountable.`,
      },
    ],
  },
  banner: {
    bannerText: 'Start your journey today. Your first step begins here.',
  },
  aboutMe: {
    header: 'Meet Your Coach',
    body: 'I’ve helped hundreds of people transform their health through simple, effective training. My goal is to make fitness sustainable for everyone.',
    body_2: '',
    aboutMeCTA: 'Join Today',
  },
  images: {},
  primaryColor: primaryColor || '#000',
})

export const getInitCheckoutPage = ({ isOneTimeProduct = false }) => {
  let benefits = ['Access to all workouts & programs', 'Form cues for improving technique']
  if (!isOneTimeProduct) {
    benefits.push('Cancel anytime in-app, hassle free')
  }

  return {
    benefits,
    heroSection: {
      mainText: 'Transform Your Fitness—Join Now',
    },
  }
}
